import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse, AiSettings, StoreTimezoneResponse } from '@/types';

/**
 * AI operating settings, per brand.
 *
 * Saved as one object, so a brand that has its own replaces the account's outright rather
 * than merging field by field — half a brand's operating hours and half the account's is
 * not a state this page can show. A brand that has saved nothing reads the account's.
 */
export const aiSettingsService = {
  async getAiSettings(scope?: { bu_id?: string }): Promise<AiSettings | null> {
    const res = await api.get<ApiResponse<{ aiSettings: AiSettings | null }>>(
      API_ENDPOINTS.BRAND.AI_SETTINGS,
      { params: scope }
    );
    return res.data.data.aiSettings;
  },

  async updateAiSettings(payload: AiSettings, scope?: { bu_id?: string }): Promise<AiSettings> {
    const res = await api.put<ApiResponse<{ aiSettings: AiSettings }>>(
      withBuScope(API_ENDPOINTS.BRAND.AI_SETTINGS, scope ?? {}),
      payload
    );
    return res.data.data.aiSettings;
  },

  /**
   * The Shopify store's IANA timezone, used to pre-fill AI Operating Hours.
   *
   * Brand-scoped, because a store belongs to one. Sending nothing showed a brand another
   * brand's timezone, and on an account whose stores are all brand-stamped the tool server
   * has nothing to prefer and answers 409.
   */
  async getStoreTimezone(scope: { bu_id?: string } = {}): Promise<StoreTimezoneResponse> {
    const res = await api.get<ApiResponse<StoreTimezoneResponse>>(
      API_ENDPOINTS.BRAND.STORE_TIMEZONE,
      { params: scope }
    );
    return res.data.data;
  }
};
